<script lang="ts" setup>
import { ref, computed, inject, Ref, onMounted, watch } from 'vue';
import { ImagesGalleryType } from '.';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { DetailsBuilderContract } from '@/components/builder/details';
import properties from '../../properties';
import { useMixins } from '@/plugins/mixins';
import { KeyValuePair } from '@/helpers/Interfaces';
import { BlueprintDefinition } from '@/components/builder/details/blueprints/BlueprintDefinition';
import FormsService from '@/modules/studio/forms/services/FormsService';
import { ResourceConfidential } from '@/modules/logito/office/services/OfficeDocuments';
import VueEasyLightbox from "vue-easy-lightbox";
import { Swiper, SwiperSlide } from 'swiper/vue';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

defineOptions({
    name: 'ImagesGalleryBlueprint',
    components: {
        ...properties,
        Swiper, SwiperSlide
    },
});

const props = defineProps({
  "blueprint": null,
  "details": null,
  "parent": null,
  "documentData": null
});

const { $filters } = useMixins();

const values = inject<Ref<any>>('values', null);
const documentFields = inject<Ref<BlueprintDefinition[]>>('documentFields', ref([]));

const blueprint = computed(() => props.blueprint);
const design = computed(() => props.details.designMode());
const fieldOptions = computed<KeyValuePair[]>(p => documentFields.value.filter(p => p.fieldType == 'ResourceConfidential' || p.fieldType == 'ResourceConfidential[]').map(p => ({key: p.control, value: p.name})));

const thumbsPerView = 5; // or whatever you use
const lightboxVisible = ref(false);
const lightboxIndex = ref(0);
const mainActiveIndex = ref(0);

const mainSwiper = ref();
const thumbsSwiper = ref();

const modules = [FreeMode, Navigation, Thumbs];
const presetsOptions = ref<KeyValuePair[]>([]);

const mainImageField = ref<KeyValuePair>();
const imagesField = ref<KeyValuePair>();
const mainImagePreset = ref<KeyValuePair>();
const thumbPreset = ref<KeyValuePair>();

watch(() => mainImageField.value, async (newValue: KeyValuePair) => blueprint.value.mainImageField = newValue?.key);
watch(() => imagesField.value, async (newValue: KeyValuePair) => blueprint.value.imagesField = newValue?.key);
watch(() => mainImagePreset.value, async (newValue: KeyValuePair) => blueprint.value.mainImagePreset = newValue?.key);
watch(() => thumbPreset.value, async (newValue: KeyValuePair) => blueprint.value.thumbPreset = newValue?.key);


const images = computed(() =>
{
    if (design?.value || !values.value) return null;

    let result = (blueprint.value.mainImageField ? values.value.result[blueprint.value.mainImageField] : null) as ResourceConfidential | ResourceConfidential[] | null;

    if (!result) result = [];

    if (!Array.isArray(result)) result = [result];

    const additionalImage = values.value.result[blueprint.value.imagesField] as ResourceConfidential | ResourceConfidential[];

    if (additionalImage)
    {
        if (Array.isArray(additionalImage)) result = [...result, ...additionalImage];
        else result.push(additionalImage);
    }

    return result.map(p => p?.url).filter(p => p != null);
});

const getImageUrl = (url: string, preset: string): string =>
{
    if (!url) return null;

    const canRead = /\.(gif|jpe?g|tiff?|png|webp|bmp|svg)$/i.test(url);

    const fileUrl = preset ? $filters.image(url, preset) : $filters.baseurl(url);

    return canRead && fileUrl ? fileUrl : null;
};

const onMainSlideChange = () =>
{
    if (mainSwiper.value)
    {
        mainActiveIndex.value = mainSwiper.value.activeIndex;

        if (thumbsSwiper.value)
        {
            const total = images.value.length;
            let target = mainActiveIndex.value - Math.floor(thumbsPerView / 2);

            if (target < 0) target = 0;

            if (target > total - thumbsPerView) target = total - thumbsPerView;

            if (target < 0) target = 0;

            thumbsSwiper.value.slideTo(target, 300, false);
        }
    }
};

const setMainSwiper = (swiper: any) => { mainSwiper.value = swiper; };
const setThumbsSwiper = (swiper: any) => { thumbsSwiper.value = swiper; };
const onThumbClick = (index: number) =>
{
    if (mainSwiper.value)
    {
        mainSwiper.value.slideTo(index);
    }
};

onMounted(async () =>
{
    if (design.value)
    {
        presetsOptions.value = (await FormsService.getPresetsOptions()).items.map(p => p.result);
        imagesField.value = fieldOptions.value.find((x) => x.key == blueprint.value.imagesField);
        mainImageField.value = fieldOptions.value.find((x) => x.key == blueprint.value.mainImageField);
        mainImagePreset.value = presetsOptions.value.find((x) => x.key == blueprint.value.mainImagePreset);
        thumbPreset.value = presetsOptions.value.find((x) => x.key == blueprint.value.thumbPreset);
    }
});

</script>

<template>
    <dynamic-details-component-wrapper :details="details" :parent="parent" :blueprint="blueprint">
        <template #default>
            <div class="h-100" v-if="images && images.length">
                <!-- Główny slider -->
                <swiper
                    @swiper="setMainSwiper"
                    @slideChange="onMainSlideChange"
                    :space-between="10"
                    navigation
                    v-bind="thumbsSwiper ? { thumbs: { swiper: thumbsSwiper } } : {}"
                    :modules="modules"
                    class="main-swiper"
                >
                    <swiper-slide
                        v-for="(image, index) in images"
                        :key="index"
                        @click="() => [lightboxIndex = index, lightboxVisible = true]"
                    >
                        <img :src="getImageUrl(image, blueprint.mainImagePreset)" />
                    </swiper-slide>
                </swiper>
                <!-- Miniatury -->
                <swiper
                    @swiper="setThumbsSwiper"
                    :space-between="10"
                    :slides-per-view="5"
                    free-mode
                    watch-slides-progress
                    :modules="modules"
                    class="thumbs-swiper"
                >
                    <swiper-slide
                        v-for="(image, index) in images"
                        :key="index"
                        :class="{ 'thumb-active': mainActiveIndex === index }"
                        @click="onThumbClick(index)"
                    >
                        <img :src="getImageUrl(image, blueprint.thumbPreset)" />
                    </swiper-slide>
                </swiper>
                <vue-easy-lightbox
                    :visible="lightboxVisible"
                    :imgs="images.map(p => getImageUrl(p, blueprint.mainImagePreset))"
                    :index="lightboxIndex"
                    @hide="lightboxVisible = false"
                />
            </div>
            <div v-else class="d-flex justify-content-center align-items-center h-100">
                <i class="far fa-image fa-8x"></i>
            </div>
        </template>
        <template #properties>
            <field-name v-model="blueprint.name" />
            <dynamic-details-accordion>
                <dynamic-details-accordion-item icon="far fa-cog" :header="$t('[[[Konfiguracja]]]')">
                    <ideo-form-group :label="$t('[[[Pole ze zdjęciami]]]')" required>
                        <ideo-select v-model="imagesField" :options="fieldOptions" />
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Pole ze zdjęciem głównym]]]')">
                        <template #description>
                            {{ $t('[[[Uzupełnij jeśli zdjęcie główne znajduje się w osobym polu]]]') }}
                        </template>
                        <ideo-select v-model="mainImageField" :options="fieldOptions" />
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Rozmiar zdjęcia głównego]]]')">
                        <ideo-select v-model="mainImagePreset" :options="presetsOptions" />
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Rozmiar minitur]]]')">
                        <ideo-select v-model="thumbPreset" :options="presetsOptions" />
                    </ideo-form-group>
                </dynamic-details-accordion-item>
                <dynamic-details-accordion-item icon="far fa-table" :header="$t('[[[Prezentacja]]]')" :open="true">
                    <field-visible :details="details" :blueprint="blueprint" />
                </dynamic-details-accordion-item>
            </dynamic-details-accordion>
        </template>
    </dynamic-details-component-wrapper>
</template>

<style lang="scss" scoped>
.fa-image {
    color: #edeff1;
}

.vel-modal {
    z-index: 99999;
}

.main-swiper {
    height: 80%;
    width: 100%;

    & .swiper-wrapper{
        align-items: center;
    }

    & .swiper-slide {
        text-align: center;
        font-size: 18px;
        // background: #444;
        display: flex;
        justify-content: center;
        align-items: center;
        background-size: cover;
        background-position: center;

        & .swiper-slide img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

}

.thumbs-swiper {
  height: 20%;
  box-sizing: border-box;
  padding: 10px 0;

    & .swiper-slide {
        width: 25%;
        height: 100%;
        opacity: 0.4; // semi-transparent by default
        transition: opacity 0.2s;

        & img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }

    & .thumb-active {
        opacity: 1; // fully opaque for the active thumb
    }
}

:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
  background: rgba(0,0,0,0.45) !important;
  border-radius: 8px !important;
  padding: 6px 12px !important;
  color: #fff !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  width: auto !important;
  height: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 10;
  border: none !important;
}
:deep(.swiper-button-next::after),
:deep(.swiper-button-prev::after) {
    font-size: 28px !important;
    color: #fff !important;
    text-shadow: 0 2px 8px rgba(0,0,0,0.8);
}

:deep(.swiper-button-next::after){
    content: '>';
}

:deep(.swiper-button-prev::after){
    content: '<';
}

:deep(.swiper-button-next:hover),
:deep(.swiper-button-prev:hover) {
  background: rgba(0,0,0,0.7) !important;
  box-shadow: 0 4px 16px rgba(0,0,0,0.5);
  color: #fff !important;
}
</style>
